import type {IElementsMap} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";
import {createSelector} from "reselect";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Base selectors for elements - direct state reads
 * These selectors perform simple property access without complex computation
 */

/** Gets all elements from the document */
export const all: DocumentSelector<IElementsMap | undefined> = (state: DocumentStoreState) => state.elements;

/** Gets an element by UID */
export const byUid: ParameterizedSelector<string, IElementsMap[string] | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid];
};

/** Gets multiple elements by UIDs */
export const byUids: ParameterizedSelector<string[], Array<IElementsMap[string]>> = (uids: string[]) => {
    return (state: DocumentStoreState) => {
        const elements = state.elements;
        if (!elements) return [];
        const result: Array<IElementsMap[string]> = [];
        for (const uid of uids) {
            const element = elements[uid];
            if (element) {
                result.push(element);
            }
        }
        return result;
    };
};

/** Gets all element UIDs - memoized to avoid creating a new array on every call */
export const uids: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.elements],
    (elements): string[] => {
        if (!elements) return emptyArray;
        return Object.keys(elements);
    },
);

/** Counts total elements */
export const count: DocumentSelector<number> = (state: DocumentStoreState) => {
    const elements = state.elements;
    if (!elements) return 0;
    return Records.count(elements);
};

/** Checks if document has any elements */
export const hasElements: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const elements = state.elements;
    if (!elements) return false;
    return !Records.isEmpty(elements);
};

/** Gets an element's label by UID */
export const labelByUid: ParameterizedSelector<string, string | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.label;
};

/** Gets an element's locked state by UID */
export const lockedByUid: ParameterizedSelector<string, boolean | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.locked;
};

/** Gets an element's properties by UID */
export const propertiesByUid: ParameterizedSelector<string, IElementsMap[string]["properties"] | undefined> = (
    uid: string,
) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.properties;
};

/** Gets an element's part_uid by UID */
export const partUidByUid: ParameterizedSelector<string, string | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.part_uid;
};

/** Gets an element's part_version_data_cache by UID */
export const partVersionDataCacheByUid: ParameterizedSelector<
    string,
    IElementsMap[string]["part_version_data_cache"] | undefined
> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.part_version_data_cache;
};

/** Gets an element's metamodule by UID */
export const metamoduleByUid: ParameterizedSelector<string, IElementsMap[string]["metamodule"] | undefined> = (
    uid: string,
) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.metamodule ?? undefined;
};

/** Gets an element's diagram_position by UID */
export const diagramPositionByUid: ParameterizedSelector<
    string,
    IElementsMap[string]["diagram_position"] | undefined
> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.diagram_position;
};

/** Gets an element's symbol_placements by UID */
export const symbolPlacementsByUid: ParameterizedSelector<
    string,
    IElementsMap[string]["symbol_placements"] | undefined
> = (uid: string) => {
    return (state: DocumentStoreState) => state.elements?.[uid]?.symbol_placements;
};
